import { AriaAttributes, AriaRole, FC, memo } from 'react';

import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';
import { WebTarget } from 'styled-components';

import { HtmlContentContainer } from './HtmlContent.styles';

type Props = {
    content: string;
    isNegative?: boolean;
    className?: string;
    dataTest?: string;
    baseComponent?: WebTarget;
    role?: AriaRole;
};

const HtmlContent: FC<Props & AriaAttributes> = ({
    content,
    baseComponent,
    isNegative,
    className,
    dataTest,
    role,
    ...ariaAttrs
}) => {
    if (!content) {
        return null;
    }

    // CMS-managed content may include iframe embeds (video, partner widgets).
    // eslint-disable-next-line sonarjs/dompurify-unsafe-config
    const sanitizedContent = sanitize(content, {
        USE_PROFILES: { html: true, svg: true },
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['allowfullscreen', 'frameborder', 'target'],
        ALLOW_SELF_CLOSE_IN_ATTR: true,
        ALLOW_ARIA_ATTR: true,
        ALLOW_DATA_ATTR: true,
        // Allows CMS-managed custom app links like com.telia.selfservice://open.
        // used base regex from config https://github.com/cure53/dompurify#can-i-configure-dompurify
        ALLOWED_URI_REGEXP:
            // eslint-disable-next-line sonarjs/regex-complexity
            /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|matrix|com\.[a-z0-9+.\-]*):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    });

    return (
        <HtmlContentContainer
            {...ariaAttrs}
            role={role}
            as={baseComponent}
            $negative={!!isNegative}
            className={classNames('html-content', className)}
            data-test={dataTest}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
    );
};

export default memo(HtmlContent);
